import {
  TrashIcon,
  TagIcon,
  CalendarDaysIcon,
  Squares2X2Icon,
} from '@heroicons/react/20/solid';
import {
  TWButton,
  TWModal,
  TWInput,
  TWSelect,
  TWDatePicker,
  ToggleSwitch,
  SegmentedToggle,
  FieldLabel,
} from '../../../components/ui';
import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import discountApi from '../../../services/discountApi';
import menuApi from '../../../services/menuApi';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ModalDelete from '../../../components/modal-delete/modal-delete';
import { MENU_FIELD_COPY } from '../constants';
import { menuIdsToArray, arrayToMenuIds, menusWereSaved } from './coupon-menus';

dayjs.extend(customParseFormat);

const DEFAULT_FORM = (companyId) => ({
  id: '',
  name: '',
  code: '',
  companyid: companyId,
  isactive: true,
  minprice: 0,
  maxdiscount: null,
  maxusagecount: 0,
  usagecount: 0,
  totaldiscount: 0,
  maxDiscountActive: false,
  rate: 0,
  fixrate: 0,
  rateOrFixRate: true,
  expiredate: dayjs(),
  menuids: [],
});

const DEFAULT_ERRORS = {
  name: false,
  code: false,
  companyid: false,
  minprice: false,
  rateOrFixRate: false,
};

const LegacyCouponUpsertModal = ({
  show,
  onClose,
  initialData,
  onSuccess,
  onError,
}) => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);

  const [form, setForm] = useState(DEFAULT_FORM(globalCompany.id));
  const [errors, setErrors] = useState(DEFAULT_ERRORS);

  const [loading, setLoading] = useState({
    upsertLoading: false,
    deleteLoading: false,
    menuLoading: false,
  });
  // The company's menus, for the Menus field (owner, 2026-10-07). A legacy
  // coupon is company-wide, so the list is never narrowed to a store.
  const [menuList, setMenuList] = useState([]);
  const [menusLoaded, setMenusLoaded] = useState(false);
  const [menusFailed, setMenusFailed] = useState(false);

  const [deletedCoupon, setDeletedCoupon] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (initialData) {
      setForm({
        id: initialData.id,
        name: initialData.name,
        code: initialData.code,
        companyid: initialData.companyid,
        isactive: initialData.isactive,
        minprice: initialData.minprice,
        maxdiscount: initialData.maxdiscount,
        maxusagecount: initialData.maxusagecount,
        usagecount: initialData.usagecount,
        totaldiscount: initialData.totaldiscount,
        maxDiscountActive: Boolean(initialData.maxdiscount),
        rate: initialData.rate,
        fixrate: initialData.fixrate,
        rateOrFixRate: Boolean(initialData.rate),
        expiredate: initialData.expiredate,
        menuids: menuIdsToArray(initialData.menuids),
      });
    } else {
      setForm(DEFAULT_FORM(globalCompany.id));
    }
    setErrors(DEFAULT_ERRORS);
  }, [show, initialData, globalCompany.id]);

  // Fetch the company's menus once per open, not on every parent render
  // (onError is a fresh function each render, so it stays out of the deps).
  useEffect(() => {
    if (show) {
      getMenuList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, globalCompany.id]);

  const getMenuList = async () => {
    try {
      setLoading((prev) => ({ ...prev, menuLoading: true }));
      setMenusLoaded(false);
      setMenusFailed(false);
      const res = await menuApi.list(`?companyid=${globalCompany.id}`);
      setMenuList(res.data?.data || []);
      setMenusLoaded(true);
    } catch (error) {
      // The coupon can still be saved as "Any menu".
      setMenuList([]);
      setMenusFailed(true);
      if (onError) onError(error);
    } finally {
      setLoading((prev) => ({ ...prev, menuLoading: false }));
    }
  };

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  // An API deployed before the dashboard ignores `menuids` and would save the
  // coupon unrestricted in silence: read it back (Discount/Get is the entity,
  // which carries menuids) and say so. A clear (menuids null) is not read
  // back: the deploy order (SQL -> api -> fe) makes that case unreachable.
  // Through Discount/List, not Discount/Get (QA on api-test, 2026-10-08):
  // the dashboard never called Get before, so the roles never carried its
  // `discount-get` permission and it answers 422 for a restaurant user.
  // List is what this page already reads, and its rows carry `menuids` and
  // `menunames`.
  const warnIfMenusNotSaved = async (body, res) => {
    if (!body.menuids) return;
    const id = body.id || res?.data?.data?.id;
    try {
      const saved = await discountApi.list(
        `?companyid=${body.companyid}&code=${encodeURIComponent(body.code)}`,
      );
      const rows = Array.isArray(saved?.data?.data) ? saved.data.data : [];
      const row = rows.find((r) => r.id === id) || rows[0];
      if (row && !menusWereSaved(body.menuids, row)) {
        message.warning(MENU_FIELD_COPY.notSaved, 6);
      }
    } catch (error) {
      // The coupon is saved; the read-back is only a net.
    }
  };

  const onFormSubmit = async () => {
    const nextErrors = {
      name: !form.name,
      code: !form.code,
      companyid: !form.companyid,
      minprice: false,
      rateOrFixRate: !form.rate && !form.fixrate,
    };
    if (
      nextErrors.name ||
      nextErrors.code ||
      nextErrors.companyid ||
      nextErrors.rateOrFixRate
    ) {
      setErrors(nextErrors);
      return;
    }
    setErrors(DEFAULT_ERRORS);

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...form };
      body.rateOrFixRate ? (body.fixrate = 0) : (body.rate = 0);
      if (!body.maxDiscountActive) body.maxdiscount = null;
      body.menuids = arrayToMenuIds(form.menuids);
      const res = await discountApi.upsert(body);
      await warnIfMenusNotSaved(body, res);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      if (onError) onError(error);
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
    }
  };

  const onCloseModal = () => {
    setErrors(DEFAULT_ERRORS);
    onClose();
  };

  const onClickDelete = () => {
    setDeletedCoupon(form);
    setShowDeleteModal(true);
  };

  const onConfirmDelete = async () => {
    if (!deletedCoupon) return;
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      await discountApi.deleteById(deletedCoupon.id);
      if (onSuccess) onSuccess();
      setShowDeleteModal(false);
      onClose();
    } catch (error) {
      if (onError) onError(error);
    } finally {
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const rateSymbol = form.rateOrFixRate ? '%' : '$';
  const rateValue = form.rateOrFixRate ? form.rate : form.fixrate;

  return (
    <>
      <TWModal
        open={show}
        onClose={onCloseModal}
        size="2xl"
        showCloseButton
        title={form.id ? 'Edit Legacy Coupon' : 'Add Legacy Coupon'}
        description="Set up a traditional discount coupon your customers can redeem at checkout."
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              {form.id ? (
                <TWButton
                  variant="danger"
                  icon={TrashIcon}
                  label="Delete"
                  disabled={loading.deleteLoading}
                  loading={loading.deleteLoading}
                  onClick={onClickDelete}
                />
              ) : null}
            </div>
            <div className="flex items-center gap-2 sm:justify-end">
              <TWButton
                variant="default"
                label="Cancel"
                disabled={loading.upsertLoading}
                onClick={onCloseModal}
              />
              <TWButton
                variant="primary"
                label={form.id ? 'Save Changes' : 'Create Coupon'}
                disabled={loading.upsertLoading}
                loading={loading.upsertLoading}
                onClick={onFormSubmit}
              />
            </div>
          </div>
        }
      >
        <div className="space-y-5">
          <TWInput
            label="Name"
            labelIcon={TagIcon}
            placeholder="Discount %5"
            value={form.name}
            onChange={(e) => onFormChange(e.target.value, 'name')}
            error={errors.name ? 'Name cannot be empty' : ''}
            required
          />

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <TWInput
              label="Coupon Code"
              placeholder="disc05"
              value={form.code}
              onChange={(e) => onFormChange(e.target.value, 'code')}
              error={errors.code ? 'Code cannot be empty' : ''}
              required
            />

            <div className="w-full">
              <div className="flex items-center justify-between gap-2">
                <FieldLabel>Discount</FieldLabel>
                <SegmentedToggle
                  size="sm"
                  value={form.rateOrFixRate}
                  onChange={(v) => onFormChange(v, 'rateOrFixRate')}
                  options={[
                    { value: true, label: 'Percent' },
                    { value: false, label: 'Fixed' },
                  ]}
                  ariaLabel="Discount type"
                />
              </div>
              <div className="relative mt-1.5">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm font-medium text-slate-500 dark:text-slate-400">
                  {rateSymbol}
                </span>
                <input
                  type="number"
                  value={rateValue || ''}
                  onChange={(e) =>
                    onFormChange(
                      e.target.value,
                      form.rateOrFixRate ? 'rate' : 'fixrate',
                    )
                  }
                  placeholder="0.00"
                  className={`block h-10 w-full rounded-md border bg-white pl-7 pr-3 text-sm text-slate-900 placeholder-slate-400 transition focus:outline-none focus:ring-1 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 ${
                    errors.rateOrFixRate
                      ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500'
                      : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500 dark:border-slate-600 dark:focus:border-sky-400 dark:focus:ring-sky-400'
                  }`}
                />
              </div>
              {errors.rateOrFixRate ? (
                <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                  Enter a rate or fixed amount
                </p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <FieldLabel>Minimum Order Value</FieldLabel>
              <div className="relative mt-1.5">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm font-medium text-slate-500 dark:text-slate-400">
                  $
                </span>
                <input
                  type="number"
                  value={form.minprice ?? ''}
                  onChange={(e) => onFormChange(e.target.value, 'minprice')}
                  placeholder="0.00"
                  className="block h-10 w-full rounded-md border border-slate-200 bg-white pl-7 pr-3 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400"
                />
              </div>
            </div>

            <div>
              <FieldLabel icon={CalendarDaysIcon}>Expiration Date</FieldLabel>
              <div className="mt-1.5">
                <TWDatePicker
                  value={
                    form.expiredate
                      ? dayjs(form.expiredate, 'YYYY-MM-DD hh:mm')
                      : null
                  }
                  format="MM/DD/YYYY"
                  onChange={(e) =>
                    onFormChange(
                      e ? dayjs(e, 'YYYY-MM-DD hh:mm') : null,
                      'expiredate',
                    )
                  }
                  style={{ width: '100%' }}
                  size="large"
                  placeholder="Select an expire date"
                />
              </div>
            </div>
          </div>

          <TWInput
            label="Max Usage Count"
            type="number"
            placeholder="0"
            value={form.maxusagecount ?? ''}
            onChange={(e) => onFormChange(e.target.value, 'maxusagecount')}
            hint="Leave at 0 for unlimited usage"
          />

          <TWSelect
            label={MENU_FIELD_COPY.label}
            labelIcon={Squares2X2Icon}
            mode="multiple"
            placeholder={
              !menusLoaded || menuList.length
                ? MENU_FIELD_COPY.placeholder
                : MENU_FIELD_COPY.noMenusCompany
            }
            // Designer brief 2026-10-07: an edited coupon's chip must stay
            // removable when the list is empty or failed, or every save 400s.
            disabled={
              loading.menuLoading || (!menuList.length && !form.menuids.length)
            }
            value={menusLoaded || menusFailed ? form.menuids : []}
            onChange={(ids) => onFormChange(ids || [], 'menuids')}
            options={menuList.map((menu) => ({
              label: menu.name,
              value: menu.id,
            }))}
            loading={loading.menuLoading}
            optionFilterProp="label"
            hint={MENU_FIELD_COPY.hint}
          />

          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-700 dark:bg-slate-800/40">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Max Order Discount
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Cap the maximum discount amount per order
                </p>
              </div>
              <ToggleSwitch
                checked={form.maxDiscountActive}
                onChange={(v) => onFormChange(v, 'maxDiscountActive')}
              />
            </div>
            {form.maxDiscountActive ? (
              <div className="relative mt-3">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm font-medium text-slate-500 dark:text-slate-400">
                  $
                </span>
                <input
                  type="number"
                  value={form.maxdiscount ?? ''}
                  onChange={(e) => onFormChange(e.target.value, 'maxdiscount')}
                  placeholder="0.00"
                  className="block h-10 w-full rounded-md border border-slate-200 bg-white pl-7 pr-3 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400"
                />
              </div>
            ) : null}
          </div>
        </div>
      </TWModal>

      <ModalDelete
        title="Delete Coupon"
        loading={loading.deleteLoading}
        open={showDeleteModal}
        onCloseRequest={() => setShowDeleteModal(false)}
        onOk={onConfirmDelete}
        item={deletedCoupon}
      />
    </>
  );
};

export default LegacyCouponUpsertModal;
